/**
 * @file components/log/weightCharts/chartUI.jsx
 * @description Shared presentational primitives and constants for the weight
 * charts — keeps individual chart components lean and avoids repeated styling.
 */

import { useEffect, useRef, useState } from "react";

// ── Cards ───────────────────────────────────────────────────────────────────

export const StatCard = ({ label, value, unit, color }) => (
  <div className="bg-white/[0.03] border border-white/5 p-4 rounded-2xl">
    <p className="text-[10px] uppercase tracking-tighter text-gray-500 font-bold mb-1">
      {label}
    </p>
    <p className={`text-2xl font-black ${color}`}>
      {value}
      <span className="text-xs ml-1 opacity-50">{unit}</span>
    </p>
  </div>
);

export const ChartCard = ({ title, children, lazy = true, minHeight = 280 }) => (
  <div className="bg-white/[0.02] border border-white/5 p-6 rounded-[2rem]">
    <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-6">
      {title}
    </h3>
    {lazy ? <LazyChart minHeight={minHeight}>{children}</LazyChart> : children}
  </div>
);

/**
 * Renders its children only once they scroll near the viewport.
 * IntersectionObserver callbacks fire *after* the first paint, so even the
 * first (already-visible) chart mounts a frame later — keeping tab navigation
 * instant and deferring off-screen charts until the user scrolls to them.
 */
const LazyChart = ({ children, minHeight = 280, rootMargin = "200px" }) => {
  const ref = useRef(null);
  // Fall back to rendering immediately when IntersectionObserver isn't available.
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return (
    <div ref={ref} style={{ minHeight }}>
      {inView ? (
        children
      ) : (
        <div
          className="w-full rounded-2xl bg-white/[0.02] animate-pulse"
          style={{ height: minHeight }}
        />
      )}
    </div>
  );
};

// ── Tooltips ─────────────────────────────────────────────────────────────────

export const CustomTooltip = ({ active, payload, label, unit = "kg" }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#121212] border border-white/10 p-3 rounded-xl shadow-2xl">
        <p className="text-[10px] text-gray-500 font-bold mb-2 uppercase">
          {label}
        </p>
        {payload.map((entry, index) => (
          <p
            key={index}
            className="text-xs font-bold"
            style={{ color: entry.color }}
          >
            {entry.name}: {Number(entry.value).toFixed(1)} {unit}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const WeeklyDeficitTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    const surplus = d.actual < 0;
    return (
      <div className="bg-[#121212] border border-white/10 p-3 rounded-xl shadow-2xl min-w-[150px]">
        <p className="text-[10px] text-gray-500 font-bold mb-2 uppercase">
          {label}
        </p>
        <p
          className="text-xs font-bold"
          style={{ color: surplus ? "#ef4444" : "#10b981" }}
        >
          {surplus ? "Surplus" : "Deficit"}:{" "}
          {Math.abs(d.actual).toLocaleString()} kcal
        </p>
        <p className="text-xs font-bold text-amber-500">
          Goal: {d.goal.toLocaleString()} kcal
        </p>
        <p className="text-[10px] text-gray-500 mt-1.5">
          {d.daysLogged} {d.daysLogged === 1 ? "day" : "days"} logged
          {d.estKgChange > 0.05
            ? ` · ~${d.estKgChange.toFixed(2)} kg lost`
            : d.estKgChange < -0.05
              ? ` · ~${Math.abs(d.estKgChange).toFixed(2)} kg gained`
              : ""}
        </p>
      </div>
    );
  }
  return null;
};
